import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const MAX_DEPTH = 4;

const slugify = (s) => s.toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({ name: "", slug: "", parent_id: "", featured: false });
  const [slugTouched, setSlugTouched] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, slug, parent_id, featured } or null

  const load = () => api.get("/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const byId = useMemo(() => {
    const m = {};
    for (const c of cats) m[c.id] = c;
    return m;
  }, [cats]);

  const depthOf = (id) => {
    let d = 0;
    let cur = id;
    const seen = {};
    while (cur && !seen[cur]) {
      seen[cur] = 1;
      d += 1;
      cur = byId[cur] ? (byId[cur].parent_id || null) : null;
    }
    return d;
  };

  const crumbsOf = (id) => {
    const path = [];
    let cur = id;
    const seen = {};
    while (cur && !seen[cur]) {
      seen[cur] = 1;
      const node = byId[cur];
      if (!node) break;
      path.unshift(node.name);
      cur = node.parent_id;
    }
    return path.join(" > ");
  };

  // Flatten the tree into [{node, level}] in DFS order
  const flatRows = useMemo(() => {
    const childrenMap = {};
    for (const c of cats) {
      const k = c.parent_id || "__root__";
      if (!childrenMap[k]) childrenMap[k] = [];
      childrenMap[k].push(c);
    }
    for (const k of Object.keys(childrenMap)) {
      childrenMap[k].sort((a, b) => a.name.localeCompare(b.name));
    }
    const out = [];
    const stack = [];
    const roots = childrenMap["__root__"] || [];
    for (let i = roots.length - 1; i >= 0; i--) stack.push({ node: roots[i], level: 0 });
    while (stack.length) {
      const cur = stack.pop();
      out.push(cur);
      const kids = childrenMap[cur.node.id] || [];
      for (let i = kids.length - 1; i >= 0; i--) stack.push({ node: kids[i], level: cur.level + 1 });
    }
    return out;
  }, [cats]);

  const eligibleParents = useMemo(() => {
    const items = cats.filter((c) => depthOf(c.id) < MAX_DEPTH);
    items.sort((a, b) => crumbsOf(a.id).localeCompare(crumbsOf(b.id)));
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats, byId]);

  const add = async () => {
    if (!form.name || !form.slug) { toast.error("Name and slug required"); return; }
    try {
      await api.post("/admin/categories", { ...form, parent_id: form.parent_id || null });
      setForm({ name: "", slug: "", parent_id: "", featured: false });
      setSlugTouched(false);
      load(); toast.success("Category added");
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.detail) || "Failed");
    }
  };

  const remove = async (cid) => {
    if (!window.confirm("Delete this category?")) return;
    try { await api.delete("/admin/categories/" + cid); load(); }
    catch (e) { toast.error((e.response && e.response.data && e.response.data.detail) || "Failed"); }
  };

  const toggleFeatured = async (cid, val) => {
    try {
      await api.patch("/admin/categories/" + cid, { featured: val });
      load();
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.detail) || "Failed");
    }
  };

  // Eligible parents for the EDIT dialog: exclude self & descendants, and any whose depth + edited subtree > MAX_DEPTH
  const isDescendantOf = (cid, ancestorId) => {
    let cur = byId[cid] ? byId[cid].parent_id : null;
    const seen = {};
    while (cur && !seen[cur]) {
      if (cur === ancestorId) return true;
      seen[cur] = 1;
      cur = byId[cur] ? byId[cur].parent_id : null;
    }
    return false;
  };

  const subtreeDepth = (cid) => {
    const kids = cats.filter((c) => c.parent_id === cid);
    if (kids.length === 0) return 1;
    let best = 1;
    for (const k of kids) {
      const d = 1 + subtreeDepth(k.id);
      if (d > best) best = d;
    }
    return best;
  };

  const editableParents = useMemo(() => {
    if (!editing) return [];
    const sub = subtreeDepth(editing.id);
    const items = cats.filter((c) => {
      if (c.id === editing.id) return false;
      if (isDescendantOf(c.id, editing.id)) return false;
      const pd = depthOf(c.id); // depth of candidate parent
      return pd + sub <= MAX_DEPTH;
    });
    items.sort((a, b) => crumbsOf(a.id).localeCompare(crumbsOf(b.id)));
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, cats, byId]);

  const saveEdit = async () => {
    if (!editing) return;
    if (!editing.name || !editing.slug) { toast.error("Name and slug required"); return; }
    try {
      await api.patch("/admin/categories/" + editing.id, {
        name: editing.name,
        slug: editing.slug,
        parent_id: editing.parent_id || null,
        featured: !!editing.featured,
      });
      setEditing(null);
      load();
      toast.success("Category updated");
    } catch (e) {
      toast.error((e.response && e.response.data && e.response.data.detail) || "Failed");
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-end justify-between border-b border-border pb-4 mb-6">
        <div>
          <h1 className="font-display text-4xl tracking-tight">Categories</h1>
          <p className="text-sm text-muted-foreground italic mt-1">
            Up to {MAX_DEPTH} levels of nesting — group titles however your readers shop.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-2 border border-border rounded-sm p-6 bg-card h-fit">
          <div className="text-sm font-medium mb-4">Add new category</div>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({
                  ...form,
                  name: e.target.value,
                  slug: slugTouched ? form.slug : slugify(e.target.value),
                })}
                className="rounded-sm mt-1.5"
                data-testid="cat-name"
              />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }}
                className="rounded-sm mt-1.5"
                data-testid="cat-slug"
              />
            </div>
            <div>
              <Label>Parent (optional — leave empty for top-level)</Label>
              <Select
                value={form.parent_id || "none"}
                onValueChange={(v) => setForm({ ...form, parent_id: v === "none" ? "" : v })}
              >
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="cat-parent">
                  <SelectValue placeholder="Top-level" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">— Top-level —</SelectItem>
                  {eligibleParents.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={"parent-opt-" + c.slug}>
                      {crumbsOf(c.id) + " (L" + depthOf(c.id) + ")"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground mt-1.5 italic">
                Only categories with room to nest are shown (max {MAX_DEPTH} levels).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={form.featured}
                onCheckedChange={(v) => setForm({ ...form, featured: v })}
                id="cat-featured"
              />
              <Label htmlFor="cat-featured">Feature on home page</Label>
            </div>
            <Button onClick={add} className="rounded-sm" data-testid="cat-add">
              <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Add category
            </Button>
          </div>
        </div>

        <div className="lg:col-span-3 border border-border rounded-sm bg-card">
          <div className="px-6 py-4 border-b border-border text-sm font-medium">
            Existing categories
          </div>
          <div>
            {flatRows.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground italic">No categories yet.</div>
            ) : (
              flatRows.map((row) => {
                const node = row.node;
                const level = row.level;
                const isDeep = level + 1 >= MAX_DEPTH;
                const indent = level * 22 + 16;
                return (
                  <div
                    key={node.id}
                    className="flex items-center justify-between py-2 pr-4 border-b border-border/60 hover:bg-muted/40"
                    style={{ paddingLeft: indent + "px" }}
                    data-testid={"cat-row-" + node.slug}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span
                        className={
                          "font-display " +
                          (level === 0 ? "text-lg" : "text-base text-foreground/85")
                        }
                      >
                        {node.name}
                      </span>
                      <span className="text-xs text-muted-foreground/70 truncate">
                        /{node.slug}
                      </span>
                      {node.featured ? (
                        <Badge
                          variant="outline"
                          className="rounded-sm text-[10px] font-normal text-accent border-accent/50"
                        >
                          Featured
                        </Badge>
                      ) : null}
                      {isDeep ? (
                        <span className="text-[10px] text-muted-foreground italic">
                          depth {level + 1}/{MAX_DEPTH}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Switch
                        checked={!!node.featured}
                        onCheckedChange={(v) => toggleFeatured(node.id, v)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditing({ id: node.id, name: node.name, slug: node.slug, parent_id: node.parent_id || "", featured: !!node.featured })}
                        data-testid={"cat-edit-" + node.slug}
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(node.id)}
                        data-testid={"cat-del-" + node.slug}
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-sm" data-testid="cat-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight">Edit category</DialogTitle>
          </DialogHeader>
          {editing ? (
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="rounded-sm mt-1.5"
                  data-testid="cat-edit-name"
                />
              </div>
              <div>
                <Label>Slug</Label>
                <Input
                  value={editing.slug}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                  className="rounded-sm mt-1.5"
                  data-testid="cat-edit-slug"
                />
                <p className="text-[11px] text-muted-foreground mt-1 italic">
                  Slug appears in the URL. Avoid spaces and special characters.
                </p>
              </div>
              <div>
                <Label>Parent</Label>
                <Select
                  value={editing.parent_id || "none"}
                  onValueChange={(v) => setEditing({ ...editing, parent_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="rounded-sm mt-1.5" data-testid="cat-edit-parent">
                    <SelectValue placeholder="Top-level" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">— Top-level —</SelectItem>
                    {editableParents.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={"cat-edit-parent-opt-" + c.slug}>
                        {crumbsOf(c.id) + " (L" + depthOf(c.id) + ")"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1 italic">
                  Self and descendants are hidden. Moves that would exceed the {MAX_DEPTH}-level limit are also hidden.
                </p>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Switch
                  checked={!!editing.featured}
                  onCheckedChange={(v) => setEditing({ ...editing, featured: v })}
                  id="cat-edit-featured"
                />
                <Label htmlFor="cat-edit-featured">Feature on home page</Label>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-sm" data-testid="cat-edit-cancel">Cancel</Button>
            <Button onClick={saveEdit} className="rounded-sm" data-testid="cat-edit-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
